import React, { useMemo, useState } from 'react';
import type { MaterialReceiptRow } from '../../types/api';
import { updateMaterialReceipt } from '../../services/materialReceiptService';
import { StatusPill } from '../StatusPill';
import { PageHeader } from '../PageHeader';
import { SearchInput } from '../SearchInput';
import { windowBounds, inWindow, windowCounts, type ScopeValue } from '../../utils/timeWindow';
import {
  CategoryTag, fmtDate, initials,
  ATTENTION_STATUSES, AttentionToggleButton, NeedsAttentionRail,
} from '../materialIntakeShared';

// MIC mail bucket creation is automatic (happens at match confirm — see
// AcePage.handleConfirmMatch) — there's no clerk-worked "Create Mail Bucket"
// stage/queue anymore, just the two physical-production steps after it.
// RegStage covers only the post-match production pipeline (persisted as
// regulatory_stage); ViewTab adds "To Verify" — the pre-match queue of
// freshly-captured receipts that still need a Job # entered via Ace.
type RegStage = 'ready_to_mail' | 'completed';
type ViewTab = 'to_verify' | RegStage;

const STAGE_TABS: Array<{ id: ViewTab; label: string }> = [
  { id: 'to_verify',     label: 'To Verify' },
  { id: 'ready_to_mail', label: 'Ready to Mail' },
  { id: 'completed',     label: 'Completed' },
];

const STAGE_LABEL: Record<ViewTab, string> = {
  to_verify: 'To Verify',
  ready_to_mail: 'Ready to Mail',
  completed: 'Completed',
};

// Any row missing/holding an unrecognized regulatory_stage lands in the first
// (earliest) stage rather than disappearing.
function stageOf(r: MaterialReceiptRow): RegStage {
  const s = r.regulatory_stage as RegStage | null;
  return s && STAGE_LABEL[s] ? s : 'ready_to_mail';
}

interface Props {
  rows: MaterialReceiptRow[];
  onSaved: (r: MaterialReceiptRow) => void;
  openAce: (r: MaterialReceiptRow) => void;
  showToast?: (m: string) => void;
}

// Shared columns, matching the Proxy/CA/Issuer tab exactly: Received,
// Category, Scan #, Client Name(s), Status, User, Actions.
function RegulatoryTable({
  rows, statusOf, onRowClick, renderActions, emptyMessage,
}: {
  rows: MaterialReceiptRow[];
  statusOf: (r: MaterialReceiptRow) => string;
  onRowClick: (r: MaterialReceiptRow) => void;
  renderActions: (r: MaterialReceiptRow) => React.ReactNode;
  emptyMessage: string;
}) {
  return (
    <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
      <thead>
        <tr>
          {['Received', 'Category', 'Scan #', 'Client Name(s)', 'Status', 'User', 'Actions'].map(h => (
            <th key={h} className="text-left text-xs font-semibold uppercase tracking-wide px-2 py-2 border-b border-trim whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} className="group hover:bg-wash transition-colors cursor-pointer" onClick={() => onRowClick(r)}>
            <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap align-top">
              {fmtDate(r.intake_date)}{r.intake_time ? <span className="text-muted"> · {r.intake_time}</span> : ''}
            </td>
            <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top"><CategoryTag category={r.job_category} /></td>
            <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink whitespace-nowrap align-top">{r.scan_number}</td>
            <td className="px-2 py-1.5 border-b border-wash align-top">
              <div className="max-w-[240px] text-xs text-copy break-words" title={r.client_names.join(', ')}>{r.client_names.join(', ')}</div>
            </td>
            <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
              <StatusPill status={statusOf(r)} />
            </td>
            <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-tint text-xs font-semibold text-ink uppercase" title={r.intake_by ?? ''}>{initials(r.intake_by)}</span>
            </td>
            <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top" onClick={e => e.stopPropagation()}>
              {renderActions(r)}
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={7} className="text-center py-12 text-muted text-sm">{emptyMessage}</td></tr>
        )}
      </tbody>
    </table>
  );
}

// A Regulatory receipt without a Job # yet (To Verify, or an exception like
// No Job Found/Received Short/On Hold) lives in the "To Verify" tab below —
// click it to open Ace and enter/confirm a Job #. Once matched, its MIC mail
// bucket is created automatically at match confirm — it lands directly in
// Ready to Mail (see AcePage.handleConfirmMatch).
export function RegulatoryIntakeQueue({ rows, onSaved, openAce, showToast }: Props) {
  const [stage, setStage] = useState<ViewTab>('to_verify');
  const [advancing, setAdvancing] = useState<string | null>(null);
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Ready to Mail requires both physical documents printed — tracked locally
  // (the print itself isn't a system-of-record change); once both are done
  // the receipt advances to Completed via the usual regulatory_stage PATCH.
  const [printed, setPrinted] = useState<Record<string, { buckSlip?: boolean; jobDetail?: boolean }>>({});
  // Period is a monitoring lens on the stats band only — it does NOT hide rows
  // from the stage tabs below, since those track a multi-day production
  // pipeline (a job received last week can still be sitting in Ready to Mail).
  const [scope, setScope] = useState<ScopeValue>({ window: 'today' });

  const bounds = useMemo(() => windowBounds(scope), [scope]);
  const windowedRows = useMemo(() => rows.filter(r => inWindow(r.intake_date, bounds)), [rows, bounds]);
  const winCounts = useMemo(() => windowCounts(rows), [rows]);

  const stats = useMemo(() => ({
    intakes: windowedRows.length,
    matched: windowedRows.filter(r => r.material_status === 'Matched').length,
    exceptions: windowedRows.filter(r => ATTENTION_STATUSES.includes(r.material_status)).length,
  }), [windowedRows]);

  const matched = useMemo(() => rows.filter(r => r.material_status === 'Matched'), [rows]);

  // To Verify tab — every Regulatory receipt not yet matched (freshly
  // captured "To Verify" rows, plus any exception still needing a Job #);
  // click one to open Ace and enter/confirm its match. Mirrors the Proxy tab's
  // main queue, which lists everything not yet Matched the same way.
  const unmatchedRows = useMemo(() => (
    rows
      .filter(r => r.material_status !== 'Matched')
      .sort((a, b) => (b.intake_date ?? '').localeCompare(a.intake_date ?? ''))
  ), [rows]);

  // Needs Attention — every open Regulatory exception, newest first. A subset
  // of unmatchedRows, pinned in its own rail regardless of the active tab.
  const attention = useMemo(() => (
    rows
      .filter(r => ATTENTION_STATUSES.includes(r.material_status))
      .sort((a, b) => (b.intake_date ?? '').localeCompare(a.intake_date ?? ''))
  ), [rows]);

  const byStage = useMemo(() => {
    const map: Record<RegStage, MaterialReceiptRow[]> = { ready_to_mail: [], completed: [] };
    matched.forEach(r => map[stageOf(r)].push(r));
    return map;
  }, [matched]);

  const filteredStageRows = useMemo(() => {
    const base = stage === 'to_verify' ? unmatchedRows : byStage[stage];
    const q = search.toLowerCase();
    if (!q) return base;
    return base.filter(r => `${r.client_names.join(' ')} ${r.scan_number}`.toLowerCase().includes(q));
  }, [byStage, unmatchedRows, stage, search]);

  // Mass print targets — every Ready to Mail row currently in view (the same
  // backlog + search filter as the table below, so the buttons print exactly
  // what's on screen; narrow the target by searching a client/scan first).
  const pendingBuckSlips = useMemo(() => filteredStageRows.filter(r => !printed[r.id]?.buckSlip), [filteredStageRows, printed]);
  const pendingJobDetails = useMemo(() => filteredStageRows.filter(r => !printed[r.id]?.jobDetail), [filteredStageRows, printed]);

  async function advanceStage(r: MaterialReceiptRow, next: RegStage) {
    setAdvancing(r.id);
    try {
      const updated = await updateMaterialReceipt(r.id, { regulatory_stage: next });
      onSaved(updated);
    } catch {
      showToast?.('Failed to update — is the backend reachable?');
    } finally {
      setAdvancing(null);
    }
  }

  function printDoc(r: MaterialReceiptRow, doc: 'buckSlip' | 'jobDetail') {
    const next = { ...printed[r.id], [doc]: true };
    setPrinted(p => ({ ...p, [r.id]: next }));
    showToast?.(doc === 'buckSlip' ? 'Buck slip printed' : 'Job detail ticket printed');
    if (next.buckSlip && next.jobDetail) advanceStage(r, 'completed');
  }

  // Batch version of printDoc — prints the given document for every pending
  // Ready to Mail row in the selected Period, advancing any row that's now
  // fully printed to Completed.
  function massPrint(doc: 'buckSlip' | 'jobDetail', targets: MaterialReceiptRow[]) {
    if (!targets.length) return;
    const merged: Record<string, { buckSlip?: boolean; jobDetail?: boolean }> = {};
    targets.forEach(r => { merged[r.id] = { ...printed[r.id], [doc]: true }; });
    setPrinted(p => ({ ...p, ...merged }));
    showToast?.(
      `${doc === 'buckSlip' ? 'Buck slips' : 'Job detail tickets'} printed for ${targets.length} receipt${targets.length === 1 ? '' : 's'}`,
    );
    targets.forEach(r => {
      if (merged[r.id].buckSlip && merged[r.id].jobDetail) advanceStage(r, 'completed');
    });
  }

  function stageActions(r: MaterialReceiptRow) {
    if (r.material_status !== 'Matched') return null; // To Verify — click the row to open Ace instead
    const s = stageOf(r);
    const busy = advancing === r.id;
    if (s === 'ready_to_mail') {
      const done = printed[r.id] || {};
      const btn = (doc: 'buckSlip' | 'jobDetail', label: string) => (
        <button onClick={() => printDoc(r, doc)} disabled={busy || done[doc]}
          className="px-2 py-1 text-xs font-medium whitespace-nowrap disabled:opacity-60"
          style={{
            borderRadius: 8,
            border: done[doc] ? '1px solid #B7E4DE' : '1px solid transparent',
            background: done[doc] ? '#E9F6F6' : 'linear-gradient(135deg,#14b8a6,#0891b2)',
            color: done[doc] ? '#063F5A' : '#FFFFFF',
          }}>
          {done[doc] ? `✓ ${label}` : `Print ${label}`}
        </button>
      );
      return (
        <div className="flex items-center gap-1.5">
          {btn('buckSlip', 'Buck Slip')}
          {btn('jobDetail', 'Job Detail')}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Global header — period + scoped stats, same band as the Proxy tab. */}
      <PageHeader
        stats={[
          { label: 'Intakes', value: stats.intakes },
          { label: 'Matched', value: stats.matched, accent: true },
          { label: 'Exceptions', value: stats.exceptions },
        ]}
        scope={scope}
        onScopeChange={setScope}
        counts={winCounts}
      />

      {/* Filter row — no Category dropdown here since every row is already Regulatory. */}
      <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filteredStageRows.length} scans</div>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search scans, clients…" width={220} />
        </div>
        {!attentionOpen && <AttentionToggleButton count={attention.length} onClick={() => setAttentionOpen(true)} />}
      </div>

      {/* Stage tabs — the production pipeline for matched Regulatory jobs. */}
      <div className="px-6 pt-4 pb-3 flex-shrink-0 flex items-center justify-between gap-3 flex-wrap gap-y-2">
        <div className="inline-flex items-center gap-0.5 p-1 bg-wash" style={{ borderRadius: 8 }}>
          {STAGE_TABS.map(t => {
            const active = stage === t.id;
            const count = t.id === 'to_verify' ? unmatchedRows.length : byStage[t.id].length;
            return (
              <button
                key={t.id}
                onClick={() => setStage(t.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors"
                style={{ borderRadius: 6, background: active ? '#FFFFFF' : 'transparent', color: active ? '#063F5A' : '#73747A', boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
                {t.label}
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: active ? '#E9F6F6' : '#EEF0F3', color: active ? '#063F5A' : '#73747A' }}>{count}</span>
              </button>
            );
          })}
        </div>
        {/* Mass print — every Ready to Mail row currently in view, so a clerk
            can batch the whole backlog (or a narrower slice, by searching a
            client/scan first) instead of printing one receipt at a time. */}
        {stage === 'ready_to_mail' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => massPrint('buckSlip', pendingBuckSlips)}
              disabled={pendingBuckSlips.length === 0}
              className="px-3 py-1.5 text-xs font-medium whitespace-nowrap border border-trim text-ink hover:bg-wash disabled:opacity-50 disabled:hover:bg-transparent"
              style={{ borderRadius: 8 }}>
              Print All Buck Slips {pendingBuckSlips.length > 0 ? `(${pendingBuckSlips.length})` : ''}
            </button>
            <button
              onClick={() => massPrint('jobDetail', pendingJobDetails)}
              disabled={pendingJobDetails.length === 0}
              className="px-3 py-1.5 text-xs font-medium whitespace-nowrap border border-trim text-ink hover:bg-wash disabled:opacity-50 disabled:hover:bg-transparent"
              style={{ borderRadius: 8 }}>
              Print All Job Details {pendingJobDetails.length > 0 ? `(${pendingJobDetails.length})` : ''}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto px-6 pb-6 min-w-0">
          <RegulatoryTable
            rows={filteredStageRows}
            statusOf={r => (stage === 'to_verify' ? r.material_status : STAGE_LABEL[stage])}
            onRowClick={openAce}
            renderActions={stageActions}
            emptyMessage={stage === 'to_verify' ? 'Nothing waiting to be matched.' : 'No receipts in this stage.'}
          />
        </div>
        {attentionOpen && (
          <NeedsAttentionRail rows={attention} onOpen={openAce} onClose={() => setAttentionOpen(false)} />
        )}
      </div>
    </div>
  );
}
